/**
 * Batch Action Helpers
 *
 * Utilities for working with batched actions. These helpers are used
 * by the document store and services to detect and process batch actions.
 *
 * Note: These are pure functions that work with action objects and don't
 * depend on any external state management library.
 */
import {AnyAction, DocumentPayloadProps, IDocumentAction} from "@buildwithflux/core";

export const BATCH_ACTION_TYPE = "BATCHED_ACTIONS";

export interface IBatchAction {
    type: typeof BATCH_ACTION_TYPE;
    payload: IDocumentAction[];
    meta: {batch: true};
}

export const isBatchAction = (action: {type: string} | IBatchAction): action is IBatchAction => {
    return "meta" in action && !!(action.meta as IBatchAction["meta"])?.batch;
};

export function batchActions(actions: AnyAction[]): IBatchAction {
    return {
        type: BATCH_ACTION_TYPE,
        payload: actions as IDocumentAction[],
        meta: {batch: true},
    };
}

type ActionCreator = {type: string};

type AtLeastOneActionCreator = [ActionCreator, ...ActionCreator[]];

type Matcher = (action: AnyAction | IBatchAction) => action is AnyAction;

export const createActionDiscriminator = (...matchAgainst: AtLeastOneActionCreator): Matcher => {
    return (action: AnyAction | IBatchAction): action is AnyAction => {
        if (isBatchAction(action)) {
            for (const a of action.payload) {
                for (const match of matchAgainst) {
                    if (match.type === a.type) return true;
                }
            }
        } else {
            for (const match of matchAgainst) {
                if (match.type === action.type) return true;
            }
        }
        return false;
    };
};

/**
 * Use this method to parse payload for an action that _may be_
 * batched. This is useful and needed especially in other action
 * processing such as subscriptions
 *
 * @param matchAction action you want to get payload from
 */
export const createActionPayloadParser = (
    matchAction: ActionCreator,
): ((action: IDocumentAction | IBatchAction) => DocumentPayloadProps | undefined) => {
    return (action: IDocumentAction | IBatchAction): DocumentPayloadProps | undefined => {
        if (isBatchAction(action)) {
            const matchedAction = action.payload.find((a) => a.type === matchAction.type);

            return matchedAction?.payload;
        } else if (action.type === matchAction.type) {
            return action.payload;
        }

        return undefined;
    };
};
